import { useMemo } from 'react';
import type { MessageRow } from '../lib/api';

interface Props {
  messages: MessageRow[];
}

/**
 * Visible proof-of-grounding strip rendered above the conversation pane
 * once the harness's silent verification gate has passed for this
 * session.
 *
 * Why this exists (Mark validated 2026-05-07): humans in this system
 * cannot independently verify whether the agent has actually loaded its
 * canonical doctrine. The Constitution's §8 gate runs before the human
 * sees anything; this strip surfaces that the gate ran and what sha was
 * checked. It is not theatre, it is respect — the human deserves to see
 * that the harness has confirmed grounding before they start trusting
 * the agent's outputs.
 *
 * Renders nothing until a `passweb_pass` row appears in the session
 * messages. On a fresh session the strip is absent until the first turn
 * lands and the gate stamp is logged.
 */
export function ContextValidatedStrip({ messages }: Props) {
  const stamp = useMemo(() => extractStamp(messages), [messages]);
  if (!stamp) return null;

  return (
    <div
      role="status"
      aria-label={`Context validated against canonical sha ${stamp.shaShort}`}
      className="flex-shrink-0 flex items-center gap-2 px-4 py-1.5
                 bg-night-800 border-b border-night-700
                 text-paper-300 font-mono text-[10px] uppercase tracking-mono-wide"
      title={`Verified at ${stamp.timeIso}\nTest pair: ${stamp.testPairId ?? 'unknown'}\nFull sha: ${stamp.shaFull ?? 'unknown'}`}
    >
      <CheckGlyph />
      <span>context validated</span>
      {stamp.shaShort && (
        <>
          <span className="text-paper-500">·</span>
          <span className="text-paper-200">{stamp.shaShort}</span>
        </>
      )}
      <span className="text-paper-500 hidden md:inline">·</span>
      <span className="text-paper-500 hidden md:inline">
        {stamp.timeShort}
      </span>
    </div>
  );
}

interface PassWebStamp {
  shaShort: string | null;
  shaFull: string | null;
  testPairId: string | null;
  timeIso: string;
  timeShort: string;
}

function extractStamp(messages: MessageRow[]): PassWebStamp | null {
  // Most recent passweb_pass wins. Sessions have at most one in practice
  // (the gate runs once per session), but be defensive.
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m?.kind !== 'passweb_pass') continue;
    let metadata: Record<string, unknown> = {};
    try {
      if (m.metadata) metadata = JSON.parse(m.metadata) as Record<string, unknown>;
    } catch {
      metadata = {};
    }
    const sha = typeof metadata.constitution_sha256 === 'string' ? metadata.constitution_sha256 : null;
    const testPairId = typeof metadata.test_pair_id === 'string' ? metadata.test_pair_id : null;
    return {
      shaShort: sha ? sha.slice(0, 12) : null,
      shaFull: sha,
      testPairId,
      timeIso: m.created_at,
      timeShort: shortTime(m.created_at),
    };
  }
  return null;
}

function shortTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function CheckGlyph() {
  // Pine-tinted check, matching the DaemonGlyph "online" hue so the two
  // verified states share a visual register.
  return (
    <svg width={12} height={12} viewBox="0 0 12 12" aria-hidden="true">
      <path
        d="M2 6 L5 9 L10 3"
        fill="none"
        stroke="oklch(72% 0.13 150)"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
